<template>
  <div>
    <v-row v-if="loading" no-gutters style="margin-top: 120px;">
      <v-col cols="12" style="height: 100vh;">
        <v-skeleton-loader type="table" />
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <h2
          class="text-h6 grey--text text--darken-3 font-weight-medium mb-2 ml-6 text-color"
          style="font-size: 20px !important;"
        >
          Signup logs
        </h2>
        <v-breadcrumbs divider=">" :items="breadcrumbs" class="py-2" />
      </v-col>
      <v-col cols="12">
        <v-card class="custom_card">
          <v-card-title>
            <h2
              class="text-h6 grey--text text--darken-3 font-weight-medium mb-2 ml-6 text-color"
              style="font-size: 20px !important;"
            >
              Logs
            </h2>
          </v-card-title>
          <v-card-text>
            <v-row no-gutters>
              <!------------------------------------------>
              <!-- FILTERS ------------------------------->
              <!------------------------------------------>
              <v-col cols="12" class="mx-3">
                <v-row class="mb-2">
                  <v-col cols="4">
                    <v-text-field
                      v-model="logsTable.filters.search"
                      outlined
                      class="rounded-lg font-weight-regular ml-5"
                      :label="$t('actions.search')"
                      prepend-inner-icon="search"
                      dense
                      hide-details
                      clearable
                      @keyup.enter="fetchSignupLogs"
                      @click:clear="clearSearch"
                    />
                  </v-col>
                  <v-col cols="4">
                    <v-autocomplete
                      v-model="logsTable.filters.organization_id"
                      :items="organizations"
                      item-text="name"
                      item-value="id"
                      label="Organizations"
                      outlined
                      dense
                      class="rounded-lg"
                      hide-details
                      clearable
                      @change="fetchSignupLogs"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12">
                <!------------------------------------------>
                <!-- DATA TABLE ---------------------------->
                <!------------------------------------------>
                <v-col cols="12">
                  <v-data-table
                    :loading="logsTable.loading"
                    :headers="logsTable.headers"
                    :items="logsTable.items"
                    :server-items-length="logsTable.total"
                    :items-per-page="logsTable.pagination.itemsPerPage"
                    :footer-props="{
                      'items-per-page-options': [5, 10, 15, 20]
                    }"
                    :no-data-text="$t('subscriptions_report.no_data')"
                    :no-results-text="$t('subscriptions_report.no_data')"
                    @update:options="logsTable.pagination = $event"
                    class="custom_card"
                  >
                    <!-- SHOWS NO DATA -->
                    <template v-slot:no-data>
                      <div class="text-center pa-5 my-5">
                        <v-img
                          :src="require('@/assets/svg/box_empty.svg')"
                          height="150"
                          max-height="150px"
                          aspect-ratio="1"
                          contain
                        />
                        <div class="grey--text">
                          {{ $t('subscriptions_report.no_data') }}
                        </div>
                      </div>
                    </template>

                    <template v-slot:item.id="{ item }">
                      {{ item.id }}
                    </template>
                    <template v-slot:item.name="{ item }">
                      {{ item.name }}
                    </template>
                    <template v-slot:item.status="{ item }">
                      <v-chip :color="getStatusChipColor(item.status)">
                        {{ item.status }}
                      </v-chip>
                    </template>
                    <template v-slot:item.createdAt="{ item }">
                      {{ item.created_at | moment('lll') }}
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <div class="d-flex justify-center align-center">
                        <v-btn icon @click="openLog(item)">
                          <v-icon>
                            visibility
                          </v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </v-col>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'Logs',
  data () {
    return {
      loading: false,
      breadcrumbs: [
        {
          text: 'Signup',
          disabled: true
        },
        {
          text: 'Logs',
          disabled: false
        }
      ],
      logsTable: {
        loading: false,
        items: [],
        total: 0,
        filters: {
          search: '',
          organization_id: null
        },
        pagination: {
          page: 1,
          rowsPerPage: 10,
          sortBy: ['id'],
          sortDesc: [false]
        },
        headers: [
          {
            text: 'ID',
            align: 'center',
            sortable: true,
            value: 'id',
            class: 'text-no-wrap header-color'
          },
          {
            text: 'Nombre',
            align: 'center',
            sortable: true,
            value: 'name',
            class: 'text-no-wrap header-color'
          },
          {
            text: 'Estado',
            align: 'center',
            sortable: true,
            value: 'status',
            class: 'text-no-wrap header-color'
          },
          {
            text: 'Organización',
            align: 'center',
            sortable: true,
            value: 'organization.name',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t('common.created_at'),
            align: 'center',
            sortable: true,
            value: 'created_at',
            class: 'text-no-wrap header-color'
          },
          {
            text: this.$t('common.actions'),
            align: 'center',
            sortable: false,
            value: 'actions',
            class: 'text-no-wrap header-color'
          }
        ]
      }
    }
  },
  async created () {
    await this.getOrganizations()
  },
  watch: {
    'logsTable.pagination': {
      async handler () {
        await this.fetchSignupLogs()
      },
      deep: true
    }
  },
  computed: {
    ...mapState({
      logs: state => state.signup.logs,
      logsTotal: state => state.signup.logsTotal,
      organizations: state => state.organization.organizations
    })
  },
  methods: {
    ...mapActions({
      fetchLogs: 'signup/fetchLogs',
      getOrganizations: 'organization/getOrganizations'
    }),
    async fetchSignupLogs () {
      const { logsTable } = this
      const { search, organization_id } = logsTable.filters
      const { page, itemsPerPage } = logsTable.pagination
      let { sortBy, sortDesc } = logsTable.pagination

      if (Array.isArray(sortBy)) {
        ;[sortBy] = sortBy
      }
      if (Array.isArray(sortDesc)) {
        ;[sortDesc] = sortDesc
      }

      logsTable.loading = true

      const payload = {
        page,
        sortBy,
        search,
        sortDesc,
        organization_id,
        limit: itemsPerPage
      }

      await this.fetchLogs(payload)
      logsTable.items = this.logs
      logsTable.total = parseInt(this.logsTotal, 10)

      logsTable.loading = false
    },
    getStatusChipColor (status) {
      const mappedStatuses = {
        success: 'success',
        error: 'error'
      }
      return mappedStatuses[status] || 'grey'
    },
    async clearSearch () {
      this.logsTable.filters.search = ''
      await this.fetchSignupLogs()
    },
    openLog (item) {
      console.log({ item })
    }
  }
}
</script>

<style lang="scss">
.custom_card {
  border-radius: 12px !important;
  border: 1px solid #d0d3db !important;
}

.text-color {
  color: #344054 !important;
}

.header-color {
  color: #2661ff !important;
}
</style>
